import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import {
  CreateAssetAssignmentDocument,
  CreateAssetAssignmentInput,
  DeactivateAssetAssignmentDocument,
} from "@hydrologiq/gql-experience/graphql"
import { emitSectionEvent, SECTION_EVENTS } from "@hydrologiq/shared/hooks/useSectionEvent"

/**
 * Hook for managing asset assignment lifecycle (create and deactivate).
 * Wraps the createAssetAssignment and deactivateAssetAssignment mutations
 * and calls refetch() after successful operations to keep the UI in sync.
 *
 * @param assetId - The ID of the asset being assigned
 * @param refetch - Callback to refetch the asset query after mutations
 */
export function useAssetAssignment(assetId: string, refetch: () => void) {
  const [createMutation, { loading: creating, error: createError }] = useMutationWithEndpoint(
    CreateAssetAssignmentDocument,
    { refetchQueries: ["GetAsset"], errorPolicy: "none" },
    "primary",
  )

  const [deactivateMutation, { loading: deactivating, error: deactivateError }] = useMutationWithEndpoint(
    DeactivateAssetAssignmentDocument,
    { refetchQueries: ["GetAsset"] },
    "primary",
  )

  const createAssignment = async (input: CreateAssetAssignmentInput) => {
    const result = await createMutation({ variables: { input } })
    if (result.errors?.length) {
      throw new Error(result.errors[0].message)
    }
    if (!result.data?.createAssetAssignment) {
      throw new Error("Failed to create assignment")
    }
    refetch()
    emitSectionEvent(SECTION_EVENTS.ASSET_ASSIGNED)
  }

  const deactivateAssignment = async (assignmentId: string) => {
    const result = await deactivateMutation({ variables: { id: assignmentId } })
    if (result.errors?.length) {
      throw new Error(result.errors[0].message)
    }
    refetch()
    emitSectionEvent(SECTION_EVENTS.ASSET_UNASSIGNED)
  }

  const error = createError?.message || deactivateError?.message || null

  return { createAssignment, deactivateAssignment, creating, deactivating, error }
}
